import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { createControllerNote } from '../../graphql/controller-notes/create-controller-note';
import { deleteControllerNote } from '../../graphql/controller-notes/delete-controller-note';
import { updateControllerNote } from '../../graphql/controller-notes/update-controller-note';
import { migrateShiftTurnoverNotes } from '../../graphql/shift-turnovers/migrate-shift-turnover-notes';
import { getControllerNotes } from '../../graphql/controller-notes/get-controller-notes';
import {
  AORControllerNoteSortOrQuery,
  ControllerNote,
  ControllerNoteConnection,
  ControllerNoteSortOrQuery,
  CreateControllerNoteInput,
  SCADAControlValue,
} from '../../model/controllerNotes/controllerNote';
import { BaseAppSyncService } from '../base-app-sync.service';
import { getClosedShiftTurnoverNotes } from '../../graphql/shift-turnovers/get-closed-shift-turnover-notes';
import { getUnitStatuses } from '../../graphql/controller-notes/get-unit-statuses';
import { getValvePositions } from '../../graphql/controller-notes/get-valve-positions';
import { ValvePosition } from '../../model/controllerNotes/valvePosition';
import { ControllerNotesBuilderService } from '../builder/controller-notes-builder.service';
import { UnitStatus } from '../../model/controllerNotes/unitStatus';
import { getControllerNote } from '../../graphql/controller-notes/get-controller-note';
import { queryForControllerNotes } from 'src/app/graphql/controller-notes/query-for-controller-notes';
import { getSCADAControlValues } from 'src/app/graphql/controller-notes/get-scada-control-values';
import { Audit } from 'src/app/model/common/audit';
import { getControllerNoteHistories } from 'src/app/graphql/controller-notes/get-controller-note-histories';
import { buildAuditHistory } from 'src/app/core/functions/common-functions';
import { EmailRequest } from 'src/app/model/admin/email-request';
import { emailControllerNote } from 'src/app/graphql/controller-notes/email-controller-note';
import { Entry } from 'src/app/model/controllerNotes/entry';
import { updateNoteEntry } from 'src/app/graphql/controller-notes/update-note-entry';
import { deleteNoteEntry } from 'src/app/graphql/controller-notes/delete-note-entry';
import { createNoteEntry } from 'src/app/graphql/controller-notes/create-note-entry';

@Injectable({
  providedIn: 'root',
})
export class ControllerNotesApiService extends BaseAppSyncService {
  private _controllerNoteBuilder = inject(ControllerNotesBuilderService);

  getControllerNotesForTurnover(
    startDate: Date,
    endDate: Date,
    aor: String
  ): Observable<ControllerNote[]> {
    return this.query(
      getControllerNotes,
      {
        StartDate: startDate ? startDate.toISOString() : null,
        EndDate: endDate ? endDate.toISOString() : null,
        AOR: aor,
        IsHistorical: false,
        TurnoverOnly: true,
        hideMaintenance: false,
      },
      (items: any[]) =>
        items.map((i) => this._controllerNoteBuilder.buildControllerNote(i)),
      'getControllerNotes'
    );
  }

  getControllerNote(
    noteId: string,
    isAorView: boolean
  ): Observable<ControllerNote> {
    return this.query(
      getControllerNote,
      { noteId },
      (item: any) =>
        this._controllerNoteBuilder.buildControllerNote(item, isAorView),
      'getControllerNote',
      'cache-first'
    );
  }

  createControllerNote(
    note: CreateControllerNoteInput,
    isAorView: boolean
  ): Observable<CreateControllerNoteInput> {
    const input = {
      abnormalOperation: note.abnormalOperation,
      addToTurnover: note.addToTurnover,
      AOR: note.AORId,
      city: note.city,
      contact: note.contactId,
      county: note.county,
      createdAt: note.createdAt?.toISOString(),
      createdBy: note.createdBy,
      category: note.category,
      facilityId: note.facilityId,
      falseAlarm: note.falseAlarm,
      maop: note.maop,
      noteTypeId: note.noteType,
      phoneNumber: note.phoneNumber,
      pipeline: note.pipeline,
      pipelineSection: note.pipelineSection,
      township: note.township,
      state: note.state,
      callerFirstName: note.callerFirstName,
      callerLastName: note.callerLastName,
      systemName: note.system,
      calloutInitiated: note.calloutInitiated,
      unit: note.unit,
      unitStatusId: note.unitStatus ? note.unitStatus.id : null,
      maintenance: note.maintenance,
      valvePositionId: note.valvePosition ? note.valvePosition.id : null,
      initialNoteEntry: note.initialNoteEntry,
      SCADAControlId: note?.SCADAControl ? note.SCADAControl : null,
    };

    return this.mutate(
      createControllerNote,
      { input },
      (i: any) => this._controllerNoteBuilder.buildControllerNote(i, isAorView),
      'createControllerNote'
    );
  }

  updateControllerNote(note: ControllerNote): Observable<ControllerNote> {
    let input =
      this._controllerNoteBuilder.buildUpdateControllerNoteInput(note);
    const inputUpdated = { ...input, createdAt: note.createdAt?.toISOString() };

    return this.mutate(
      updateControllerNote,
      {
        id: note.id,
        input: inputUpdated,
      },
      (i: any) => this._controllerNoteBuilder.buildControllerNote(i),
      'updateControllerNote'
    );
  }

  deleteControllerNote(id: String): Observable<any> {
    return this.mutate(
      deleteControllerNote,
      { id },
      (i: any) => i,
      'deleteControllerNote'
    );
  }

  migrateShiftTurnoverNotes(shiftTurnoverId: String): Observable<boolean> {
    return this.mutate(
      migrateShiftTurnoverNotes,
      { shiftTurnoverId },
      (i: any) => i.value,
      'migrateShiftTurnoverNotes'
    );
  }

  getClosedShiftTurnoverNotes(
    shiftTurnoverId: String
  ): Observable<ControllerNote[]> {
    return this.query(
      getClosedShiftTurnoverNotes,
      { shiftTurnoverId },
      (items: any[]) =>
        items.map((i) => this._controllerNoteBuilder.buildControllerNote(i)),
      'getClosedShiftTurnoverNotes'
    );
  }

  getUnitStatuses(aorId: String | null): Observable<UnitStatus[]> {
    return this.query(
      getUnitStatuses,
      { aorId },
      (items: any[]) =>
        items.map((i) => this._controllerNoteBuilder.buildUnitStatuses(i)),
      'getUnitStatuses',
      'cache-first'
    );
  }

  getValvePositions(aorId: string | null): Observable<ValvePosition[]> {
    return this.query(
      getValvePositions,
      { aorId },
      (items: any[]) =>
        items.map((i) => this._controllerNoteBuilder.buildValvePositions(i)),
      'getValvePositions',
      'cache-first'
    );
  }

  queryForControllerNotes(
    page: number,
    limit: number,
    query: ControllerNoteSortOrQuery | AORControllerNoteSortOrQuery,
    sort: ControllerNoteSortOrQuery | AORControllerNoteSortOrQuery,
    isAorView: boolean
  ): Observable<ControllerNoteConnection> {
    return this.query(
      queryForControllerNotes,
      { page, limit, query, sort },
      (response: any) => {
        return {
          items: response.items.map((value: any) =>
            this._controllerNoteBuilder.buildControllerNote(value, isAorView)
          ),
          totalRecords: response.totalRecords,
        };
      },
      'queryForControllerNotes'
    );
  }

  getSCADAControlValues(): Observable<SCADAControlValue[]> {
    return this.query(
      getSCADAControlValues,
      {},
      (items: any[]) =>
        items.map((i) => this._controllerNoteBuilder.buildSCADAControlValue(i)),
      'getSCADAControlValues',
      'cache-first'
    );
  }

  getControllerNotesHistories(parentId: string): Observable<Audit[]> {
    return this.query(
      getControllerNoteHistories,
      { parentId },
      (histories: any[]) => buildAuditHistory(histories),
      'getControllerNoteHistories'
    );
  }

  emailControllerNote(request: EmailRequest): Observable<any> {
    return this.mutate(
      emailControllerNote,
      { input: request },
      (i: any) => i,
      'emailControllerNote'
    );
  }

  createNoteEntry(
    entry: Entry,
    isAorView: boolean,
    aorName: string
  ): Observable<Entry> {
    return this.mutate(
      createNoteEntry,
      {
        entryTime: Math.floor(entry.time.valueOf() / 1000),
        entryText: entry.note,
        controllerNotesId: entry.controllerNotesId,
      },
      (i: any) =>
        this._controllerNoteBuilder.buildControllerNoteEntry(
          i,
          isAorView,
          aorName
        ),
      'createNoteEntry'
    );
  }

  updateNoteEntry(
    entry: Entry,
    isAorView: boolean,
    aorName: string
  ): Observable<Entry> {
    return this.mutate(
      updateNoteEntry,
      {
        id: entry.id,
        entryTime: Math.floor(entry.time.valueOf() / 1000),
        entryText: entry.note,
      },
      (i: any) =>
        this._controllerNoteBuilder.buildControllerNoteEntry(
          i,
          isAorView,
          aorName
        ),
      'updateNoteEntry'
    );
  }

  deleteNoteEntry(id: String): Observable<boolean> {
    return this.mutate(
      deleteNoteEntry,
      { id },
      (i: any) => i,
      'deleteNoteEntry'
    );
  }
}
